import type { T3ActivityItem } from './agent-protocol';

/**
 * SF-T-273: a T3 thread whose worktree folder is gone (tidied after a merge, deleted by hand): every message to it
 * fails with "Provider session failed to open: This thread's workspace folder no longer exists or is not a
 * directory: <path>. Restore the folder at this path before retrying." The task's Agent tab offers Continue in a
 * new worktree under that error, and the hub checks the thread says so before it starts one.
 */

const FOLDER_GONE_RE = /workspace folder no longer exists or is not a directory: (\/.*?)\. Restore the folder at this path/;

/** Thread statuses with a turn starting or going: a message is on its way, so it isn't stuck (yet). */
const BUSY_THREAD_STATUSES = new Set(['preparing', 'queued', 'starting', 'running', 'waiting', 'in_progress', 'inProgress']);

/** The missing folder an error's text names, or null when it's another error. */
export function folderGonePath(text: string | null | undefined): string | null {
  const path = text ? FOLDER_GONE_RE.exec(text)?.[1] : undefined;
  return path && !path.includes('\n') ? path : null;
}

export interface FolderGone {
  /** The missing folder. */
  path: string;
  /** The error item. */
  errorItemId: string;
  errorPosition: number;
  /** The message to resend: the newest one written, after the error or before it (null when the read doesn't reach one). */
  message: { itemId: string; position: number; text: string; textTruncated: boolean } | null;
}

/**
 * The thread is stuck on its missing folder: its newest error is that one, nothing has worked since (only more
 * messages that failed the same way), and no turn is starting. Null otherwise. `items` oldest first.
 */
export function folderGoneOf(items: readonly T3ActivityItem[], threadStatus: string | null): FolderGone | null {
  if (threadStatus && BUSY_THREAD_STATUSES.has(threadStatus)) return null;
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i]!;
    // Messages sent since, which failed too.
    if (item.type === 'user_message') continue;
    if (item.type !== 'error') return null;
    const path = folderGonePath(item.text);
    if (!path) return null;
    // The message to resend: the newest one written (one sent after the error failed too, or never started),
    // else the error's own turn's, else the newest before it.
    const after = items.slice(i + 1).filter((m) => m.type === 'user_message');
    const before = items.slice(0, i).filter((m) => m.type === 'user_message');
    const sent = after.at(-1) ?? [...before].reverse().find((m) => item.runId && m.runId === item.runId) ?? before.at(-1) ?? null;
    return {
      path,
      errorItemId: item.itemId,
      errorPosition: item.position,
      message: sent ? { itemId: sent.itemId, position: sent.position, text: sent.text ?? '', textTruncated: sent.textTruncated } : null,
    };
  }
  return null;
}
